/*
 * Områdesfiltret på kollektionssidorna (sections/collection-area-filter.liquid):
 * byter område utan att ladda om sidan.
 *
 * Cirklarna är vanliga länkar till /collections/<kollektion>/<tagg>, så utan JS laddas
 * sidan om som förut. Med JS hämtas bara de sektioner som ändras – brödsmulor,
 * kollektionsbannern, områdesfiltret och produktsektionen – via Section Rendering API
 * (?sections=) och byts på plats. Adressen uppdateras med history.pushState, och
 * bakåt/framåt i webbläsaren byter tillbaka.
 *
 * Svaret hämtas redan när pekaren hamnar över en cirkel (eller vid tryck/fokus), så att
 * bytet oftast är klart i samma stund som man klickar.
 */

/** Sektionerna som byts vid ett områdesbyte (hittas via ett element i varje sektion). */
const AREA_FILTER_SECTION_SELECTORS = [
  '.section-breadcrumb',
  '.collection-hero__title',
  'area-filter',
  '.collection-tabs-grid',
  '.collection__products',
];

/** Raden som scrollar i sidled på mobil: områdesfiltret eller kategoriremsans problemcirklar. */
const AREA_FILTER_SCROLL_SELECTOR = 'area-filter .area-filter__list, area-filter .collection-list__items';

/** Produktsektionen som tonas ned under hämtningen: flikarna eller produktgriden. */
const AREA_FILTER_PRODUCTS_SELECTOR = '.collection-tabs-grid, .collection__products';

/** @type {Map<string, Promise<Record<string, string | null>>>} */
const areaFilterCache = new Map();

/** Skript som redan körts på sidan – nya sektioner kan behöva t.ex. product-tabs.js. */
const areaFilterLoadedScripts = new Set([...document.scripts].map((script) => script.src).filter(Boolean));

let areaFilterNavigation = 0;

/**
 * @returns {string[]} Sektions-id:n (utan prefixet shopify-section-) som finns på sidan
 */
function getAreaFilterSectionIds() {
  const ids = new Set();
  for (const selector of AREA_FILTER_SECTION_SELECTORS) {
    const section = document.querySelector(selector)?.closest('.shopify-section');
    if (section) ids.add(section.id.replace(/^shopify-section-/, ''));
  }
  return [...ids];
}

/**
 * @param {string} url
 * @returns {Promise<Record<string, string | null>>}
 */
async function requestAreaFilterSections(url) {
  const response = await fetch(url, { credentials: 'same-origin' });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

/**
 * Hämtar sektionerna för en områdesadress. Samma adress hämtas bara en gång per sidvisning.
 * @param {string} href
 * @param {string[]} sectionIds
 * @returns {Promise<Record<string, string | null>>}
 */
function fetchAreaFilterSections(href, sectionIds) {
  const url = new URL(href, window.location.origin);
  url.searchParams.set('sections', sectionIds.join(','));
  const key = url.toString();

  if (!areaFilterCache.has(key)) {
    const request = requestAreaFilterSections(key);
    areaFilterCache.set(key, request);
    request.catch(() => areaFilterCache.delete(key));
  }
  return areaFilterCache.get(key);
}

/**
 * Byter innehållet i en sektion mot det nyrenderade. Temats komponenter (flikar,
 * köpknappar, snabbvisning) är custom elements och startar av sig själva när de sätts in.
 * @param {string} id
 * @param {string | null | undefined} html
 */
function swapAreaFilterSection(id, html) {
  if (typeof html !== 'string') return;

  const current = document.getElementById(`shopify-section-${id}`);
  const next = new DOMParser().parseFromString(html, 'text/html').getElementById(`shopify-section-${id}`);
  if (!current || !next) return;

  current.replaceChildren(...next.childNodes);

  // Inlästa <script>-taggar körs inte – ladda de skript som sidan inte redan har
  for (const script of current.querySelectorAll('script[src]')) {
    if (areaFilterLoadedScripts.has(script.src)) continue;
    areaFilterLoadedScripts.add(script.src);
    const freshScript = document.createElement('script');
    freshScript.src = script.src;
    document.head.append(freshScript);
  }
}

/**
 * @returns {HTMLElement} Den dolda statusraden som skärmläsare läser upp efter ett byte
 */
function getAreaFilterStatus() {
  let status = document.getElementById('AreaFilterStatus');
  if (status) return status;

  status = document.createElement('div');
  status.id = 'AreaFilterStatus';
  status.className = 'visually-hidden';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  document.body.append(status);
  return status;
}

/**
 * Efter bytet: rubrik och brödsmula med cirkelns etikett, sidtitel och uppläsning.
 */
function applyAreaFilterState() {
  const filter = document.querySelector('area-filter');
  if (!filter) return;

  const { currentLabel = '', currentTag = '', pageTitle = '', statusTemplate = '' } = filter.dataset;

  // Samma sak som det inbäddade skriptet i sektionen gör vid vanlig sidladdning
  if (currentLabel && currentLabel !== currentTag) {
    for (const element of document.querySelectorAll('[data-collection-title], [data-active-tag]')) {
      element.textContent = currentLabel;
    }
  }

  if (pageTitle) document.title = pageTitle;

  const heading = document.querySelector('[data-collection-title]')?.textContent.trim();
  if (!heading || !statusTemplate) return;

  const status = getAreaFilterStatus();
  status.textContent = '';
  requestAnimationFrame(() => {
    status.textContent = statusTemplate.replace('__AREA__', heading);
  });
}

/**
 * Mobilens cirkelrad scrollar i sidled: skjut in den valda cirkeln i bild om den ligger
 * utanför. Bara i sidled – sidan i sig scrollar inte.
 * @param {boolean} smooth
 */
function revealCurrentArea(smooth) {
  const list = document.querySelector(AREA_FILTER_SCROLL_SELECTOR);
  const current = list?.querySelector('.area-filter__link--current, [aria-current="true"]');
  if (!list || !current || list.scrollWidth <= list.clientWidth) return;

  const listRect = list.getBoundingClientRect();
  const currentRect = current.getBoundingClientRect();
  if (currentRect.left >= listRect.left && currentRect.right <= listRect.right) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  list.scrollTo({
    left: list.scrollLeft + (currentRect.left - listRect.left) - (listRect.width - currentRect.width) / 2,
    behavior: smooth && !reducedMotion ? 'smooth' : 'auto',
  });
}

/** Appar som behöver köras om på nya kort (Judge.me:s stjärnor). */
function refreshAreaFilterApps() {
  try {
    window.jdgm?.batchRenderBadges?.();
  } catch (error) {
    // Stjärnorna är redan renderade på servern – Judge.me finputsar bara
  }
}

/**
 * @param {string} href
 * @param {{ push?: boolean, focusIndex?: number }} [options]
 */
async function navigateAreaFilter(href, { push = true, focusIndex = -1 } = {}) {
  const navigation = ++areaFilterNavigation;
  const sectionIds = getAreaFilterSectionIds();
  const productsSection = document.querySelector(AREA_FILTER_PRODUCTS_SELECTOR)?.closest('.shopify-section');

  productsSection?.classList.add('area-filter-loading');
  productsSection?.setAttribute('aria-busy', 'true');

  /** @type {Record<string, string | null>} */
  let sections;
  try {
    sections = await fetchAreaFilterSections(href, sectionIds);
  } catch (error) {
    // Nätverksfel: gör ett vanligt sidbyte i stället
    window.location.assign(href);
    return;
  }

  // Ett senare klick har redan tagit över
  if (navigation !== areaFilterNavigation) return;

  const scrollLeft = document.querySelector(AREA_FILTER_SCROLL_SELECTOR)?.scrollLeft ?? 0;

  for (const id of sectionIds) {
    swapAreaFilterSection(id, sections[id]);
  }

  productsSection?.classList.remove('area-filter-loading');
  productsSection?.removeAttribute('aria-busy');

  const list = document.querySelector(AREA_FILTER_SCROLL_SELECTOR);
  if (list) list.scrollLeft = scrollLeft;
  revealCurrentArea(true);

  if (push) history.pushState({ areaFilter: true }, '', href);

  applyAreaFilterState();
  refreshAreaFilterApps();

  if (focusIndex >= 0) {
    document.querySelectorAll('area-filter a[href]')[focusIndex]?.focus({ preventScroll: true });
  }

  document.dispatchEvent(new CustomEvent('collection:rerendered'));
}

window.addEventListener('popstate', (event) => {
  if (!event.state?.areaFilter) return;
  navigateAreaFilter(window.location.href, { push: false });
});

class AreaFilter extends HTMLElement {
  connectedCallback() {
    // Temaredigeraren laddar om sektionerna själv
    if (window.Shopify?.designMode) return;

    this.addEventListener('click', this.#onClick);
    this.addEventListener('pointerover', this.#onIntent);
    this.addEventListener('focusin', this.#onIntent);
    this.addEventListener('touchstart', this.#onIntent, { passive: true });

    getAreaFilterStatus();

    // Delad länk till ett område på mobil: visa den valda cirkeln direkt
    if (!areaFilterNavigation) requestAnimationFrame(() => revealCurrentArea(false));

    // Första sidvisningen blir en egen historikpost, så att bakåt hittar tillbaka hit
    if (!history.state?.areaFilter) {
      history.replaceState({ ...(history.state ?? {}), areaFilter: true }, '');
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('pointerover', this.#onIntent);
    this.removeEventListener('focusin', this.#onIntent);
    this.removeEventListener('touchstart', this.#onIntent);
  }

  /**
   * @param {Event} event
   * @returns {HTMLAnchorElement | null} Länken om den byter område inom samma kollektion
   */
  #getAreaLink(event) {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return null;

    const url = new URL(link.href);
    const base = new URL(this.dataset.baseUrl || window.location.pathname, window.location.origin);
    if (url.origin !== window.location.origin || !url.pathname.startsWith(base.pathname)) return null;

    return link;
  }

  /** @param {Event} event */
  #onIntent = (event) => {
    const link = this.#getAreaLink(event);
    if (!link) return;
    fetchAreaFilterSections(link.href, getAreaFilterSectionIds()).catch(() => {});
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = this.#getAreaLink(event);
    if (!link) return;

    event.preventDefault();
    // Fokus flyttas till den nya cirkeln bara för tangentbordsanvändare (länken hade synligt
    // fokus när den valdes) – vid musklick ska ingen fokusram dyka upp efter bytet.
    const links = [...this.querySelectorAll('a[href]')];
    const keyboard = link.matches(':focus-visible');
    navigateAreaFilter(link.href, { push: true, focusIndex: keyboard ? links.indexOf(link) : -1 });
  };
}

if (!customElements.get('area-filter')) {
  customElements.define('area-filter', AreaFilter);
}
